import * as React from "react";
import { gRecaptchaResponseName } from "config/constants";
import { ContactRequest, Property } from "types";
import { useForm } from "react-hook-form";
import { useAsync, useRecaptcha } from "hooks";
import { Paths } from "config/paths";
import { client } from "utils";
import toast, { CheckmarkIcon, ErrorIcon } from "react-hot-toast";
import {
  Box,
  BoxProps,
  Heading,
  FormGroup,
  Label,
  Input,
  FormHelperError,
  Textarea,
  Button,
  Flex,
} from "../atoms";
import { Card } from "../molecules";

const emailPattern =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

type FormValues = {
  name: string;
  email: string;
  phone: string;
  message: string;
};

type Props = Omit<BoxProps, "property"> & {
  title?: string;
  property?: Property;
  defaultMessage?: string;
};

const ContactForm: React.FC<Props> = ({
  title,
  property,
  defaultMessage = "",
  ...props
}) => {
  const runRecaptcha = useRecaptcha();
  const emailSubmit = useAsync({
    onSuccess: () => toast.success("Su consulta fue enviada con exito"),
  });

  const sendEmail = (values: FormValues, token: string) => {
    return client(Paths.api.email, {
      method: "POST",
      data: {
        ...values,
        property,
        [gRecaptchaResponseName]: token,
      } as ContactRequest,
    });
  };

  const onSubmit = async (values: FormValues) => {
    const token = await runRecaptcha();
    await emailSubmit.run(sendEmail(values, token));
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: "onBlur",
    shouldFocusError: true,
  });

  return (
    <Card {...props}>
      {title ? (
        <Heading as="h3" css={{ marginBottom: "$5" }}>
          {title}
        </Heading>
      ) : null}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit(onSubmit)(event);
        }}
        noValidate
      >
        <Flex gap="3" direction="column">
          <FormGroup error={errors.name}>
            <Label htmlFor="name">Nombre</Label>
            <Input
              id="name"
              autoComplete="name"
              placeholder="Juan Pérez"
              maxLength={64}
              {...register("name", {
                required: {
                  value: true,
                  message: "Por favor ingrese un nombre.",
                },
              })}
            />
            <FormHelperError />
          </FormGroup>
          <FormGroup error={errors.email}>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="juan@ejemplo.com"
              maxLength={320}
              {...register("email", {
                required: {
                  value: true,
                  message: "Por favor ingrese un email de contacto.",
                },
                pattern: {
                  value: emailPattern,
                  message: "Por favor ingrese un email válido.",
                },
              })}
            />
            <FormHelperError />
          </FormGroup>
          <FormGroup error={errors.phone}>
            <Label htmlFor="phone">Teléfono</Label>
            <Input
              id="phone"
              type="tel"
              autoComplete="tel"
              placeholder="+598 12 345 678"
              maxLength={20}
              {...register("phone", {
                required: {
                  value: true,
                  message: "Por favor ingrese un teléfono de contacto.",
                },
              })}
            />
            <FormHelperError />
          </FormGroup>
          <FormGroup error={errors.message}>
            <Label htmlFor="message">Mensaje</Label>
            <Textarea
              id="message"
              {...register("message", {
                required: {
                  value: true,
                  message: "Por favor ingrese un mensaje.",
                },
              })}
              defaultValue={defaultMessage}
              maxRows={10}
              maxLength={1000}
            />
            <FormHelperError />
          </FormGroup>
          {emailSubmit.isError ? (
            <Flex gap="2" align="center">
              <div>
                <ErrorIcon />
              </div>{" "}
              <span>
                Hubo un error al enviar la consulta, por favor{" "}
                <Box
                  as="span"
                  css={{ color: "$primary", cursor: "pointer" }}
                  onClick={() => emailSubmit.reset()}
                >
                  intente de nuevo.
                </Box>
              </span>
            </Flex>
          ) : emailSubmit.isSuccess ? (
            <Flex gap="3" align="center">
              <div>
                <CheckmarkIcon />
              </div>{" "}
              Gracias por enviar tu consulta.
              <br />
              Te responderemos a la brevedad.
            </Flex>
          ) : (
            <Button type="submit" loading={emailSubmit.isLoading}>
              {emailSubmit.isLoading ? "Enviando..." : "Enviar consulta"}
            </Button>
          )}
        </Flex>
      </form>
    </Card>
  );
};

export { ContactForm };
